import { CheckIcon, CrossIcon } from "$components/icons";
import { DatePicker } from "$components/rhf-fields";
import { RHFTextField } from "$routes/inventory/_lib/rhf-text-field";
import SkuSearchInput from "$routes/inventory/_lib/sku-search-input";
import { gql } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Grid,
  IconButton,
  makeStyles,
  Typography,
} from "@material-ui/core";
import { format } from "date-fns";
import { FormProvider, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import * as Yup from "yup";
import { useAddStockTakeLotMutation } from "./__generated__/ongoing-stock-take-add-item.gql";

gql`
  mutation addStockTakeLot($input: AddStockTakeLotInput!) {
    addStockTakeLot(input: $input) {
      id
      stockTake {
        id
        aggregateInfo {
          totalLotCount
          reviewedLotCount
        }
      }
    }
  }
`;

const SELECT_ITEM_VALIDATION = Yup.object({
  id: Yup.string().required(),
  name: Yup.string().required(),
}).required();

const validationSchema = Yup.object({
  expiryDate: Yup.date().required(),
  reviewedQuantity: Yup.number().required(),
  sku: SELECT_ITEM_VALIDATION,
});

type FormValues = Yup.Asserts<typeof validationSchema>;

const useStyles = makeStyles((_theme) => ({
  button: {
    padding: "5px",
    width: "32px",
    height: "32px",
  },
  buttonWrapper: {
    display: "flex",
    justifyContent: "flex-end",
  },
}));

interface Props {
  stockTakeId: string;
  facilityId: string;
  onCancel: () => void;
  onComplete: () => void;
  onError: (err: Error) => void;
}

const OngoingStockTakeAddItem: FC<Props> = ({
  stockTakeId,
  facilityId,
  onCancel,
  onComplete,
  onError,
}) => {
  const { formatMessage } = useIntl();
  const [addLot] = useAddStockTakeLotMutation({
    onCompleted: onComplete,
    onError,
  });
  const classes = useStyles();
  const formMethods = useForm<FormValues>({
    defaultValues: {
      expiryDate: null,
      reviewedQuantity: null,
      sku: null,
    },
    resolver: yupResolver(validationSchema),
    mode: "onTouched",
  });

  const submit = async ({ expiryDate, reviewedQuantity, sku }) => {
    await addLot({
      variables: {
        input: {
          stockTakeId,
          facilityId,
          expiryDate: format(expiryDate, "yyyy-MM-dd"),
          reviewedQuantity,
          skuId: sku.id,
        },
      },
    });
  };

  return (
    <Box>
      <form onSubmit={formMethods.handleSubmit(submit)}>
        <FormProvider {...formMethods}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h5">
                <FormattedMessage
                  id="inventory.stockTake.ongoing.addItem.header"
                  defaultMessage="Add item"
                />
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <SkuSearchInput
                name="sku"
                analyticsEventName="inventory_management.stockTake.addItem.skuSearch"
                required
              />
            </Grid>
            <Grid item xs={4} md={2}>
              <DatePicker
                name="expiryDate"
                label={formatMessage({
                  id: "inventory.stockTake.ongoing.expiryDate.label",
                  defaultMessage: "Expiry Date",
                })}
                views={["year", "month"]}
                fullWidth
                required
                disablePast
                format="MM/yyyy"
              />
            </Grid>
            <Grid item xs={4} md={2}>
              <RHFTextField
                name="reviewedQuantity"
                type="number"
                label={formatMessage({
                  id: "inventory.stockTake.ongoing.reviewedQuantity.label",
                  defaultMessage: "Quantity",
                })}
                data-testid="reviewed-quantity-field"
                variant="outlined"
                inputProps={{
                  min: 1,
                  max: 2147483647,
                }}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={4} md={2}>
              <div className={classes.buttonWrapper}>
                <IconButton
                  aria-label="cancel"
                  aria-controls="cancel"
                  onClick={onCancel}
                >
                  <CrossIcon className={classes.button} fill="#004F71" />
                </IconButton>
                <IconButton
                  type="submit"
                  aria-label="confirm"
                  aria-controls="confirm"
                >
                  <CheckIcon className={classes.button} fill="#004F71" />
                </IconButton>
              </div>
            </Grid>
          </Grid>
        </FormProvider>
      </form>
    </Box>
  );
};

export default OngoingStockTakeAddItem;
